import mixpanel from 'mixpanel-browser';
import { AnalyticsEvents, type AnalyticsEventName, type EventPropertiesMap } from './events';

export type AnalyticsEnvironment = 'development' | 'staging' | 'production';

export interface AnalyticsConfig {
  token: string;
  environment: AnalyticsEnvironment;
  enabled: boolean;
  debug: boolean;
  apiHost?: string;
  persistence?: 'localStorage' | 'cookie' | 'memory';
}

export interface UserTraits {
  user_id: string;
  email?: string;
  name?: string;
  plan?: string;
  user_type?: string;
  created_at?: string;
  [key: string]: string | number | boolean | undefined;
}

const CONSENT_KEY = 'mixpanel_analytics_consent';
const ANON_ID_KEY = 'mixpanel_anonymous_id';

let initialized = false;
let config: AnalyticsConfig | null = null;
let dataSharingApproved = true;

function resolveEnvironment(): AnalyticsEnvironment {
  const env = import.meta.env.VITE_APP_ENV;
  if (env === 'staging' || env === 'production' || env === 'development') {
    return env;
  }
  return import.meta.env.PROD ? 'production' : 'development';
}

function getTokenForEnv(env: AnalyticsEnvironment): string {
  const map: Record<AnalyticsEnvironment, string | undefined> = {
    development: import.meta.env.VITE_MIXPANEL_TOKEN_DEV,
    staging: import.meta.env.VITE_MIXPANEL_TOKEN_STAGING,
    production: import.meta.env.VITE_MIXPANEL_TOKEN,
  };
  return map[env] ?? '';
}

function resolveConfig(overrides?: Partial<AnalyticsConfig>): AnalyticsConfig {
  const environment = overrides?.environment ?? resolveEnvironment();

  return {
    token: overrides?.token ?? getTokenForEnv(environment),
    environment,
    enabled:
      overrides?.enabled ??
      import.meta.env.VITE_MIXPANEL_ENABLED === 'true',
    debug:
      overrides?.debug ??
      import.meta.env.VITE_MIXPANEL_DEBUG === 'true',
    apiHost: overrides?.apiHost ?? import.meta.env.VITE_MIXPANEL_API_HOST,
    persistence: overrides?.persistence ?? 'localStorage',
  };
}

function createAnonymousId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `anon_${crypto.randomUUID()}`;
  }
  return `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function getAnonymousId(): string {
  if (typeof window === 'undefined') return 'anon_ssr';
  let id = localStorage.getItem(ANON_ID_KEY);
  if (!id) {
    id = createAnonymousId();
    localStorage.setItem(ANON_ID_KEY, id);
  }
  return id;
}

export function hasMixpanelConsent(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'granted') return true;
  if (stored === 'denied') return false;
  return dataSharingApproved;
}

/** Sync with UserPreferences.data_sharing and GA consent bridge */
export function setMixpanelConsent(granted: boolean): void {
  dataSharingApproved = granted;
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');

  if (!initialized) {
    if (granted) initMixpanelAnalytics();
    return;
  }

  if (granted) {
    mixpanel.opt_in_tracking();
    mixpanel.identify(getAnonymousId());
  } else {
    mixpanel.opt_out_tracking();
    mixpanel.reset();
  }
}

export function initMixpanelAnalytics(overrides?: Partial<AnalyticsConfig>): boolean {
  if (initialized) return true;

  config = resolveConfig(overrides);

  if (!config.enabled || !config.token) {
    if (config.debug) {
      console.info('[mixpanel] disabled — missing token or VITE_MIXPANEL_ENABLED');
    }
    return false;
  }

  if (!hasMixpanelConsent()) {
    if (config.debug) console.info('[mixpanel] waiting for user consent');
    return false;
  }

  mixpanel.init(config.token, {
    debug: config.debug,
    track_pageview: false,
    persistence: config.persistence,
    api_host: config.apiHost,
    ignore_dnt: false,
    loaded: (mp) => {
      mp.register({
        app_env: config!.environment,
        app_version: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
        platform: 'web',
        product: 'fixnow',
      });
      mp.identify(getAnonymousId());
    },
  });

  initialized = true;
  return true;
}

function baseProps(): Record<string, unknown> {
  return {
    app_env: config?.environment ?? 'unknown',
    timestamp: new Date().toISOString(),
    page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    page_url: typeof window !== 'undefined' ? window.location.href : undefined,
  };
}

function stripSensitiveProps<T extends Record<string, unknown> | undefined>(
  props: T,
): T {
  if (!props) return props;
  const clone = { ...props } as Record<string, unknown>;
  delete clone.email;
  delete clone.message_body;
  delete clone.content;
  return clone as T;
}

export function trackMixpanelEvent<E extends AnalyticsEventName>(
  event: E,
  properties?: EventPropertiesMap[E],
): void {
  if (!initialized || !config?.enabled) {
    if (config?.debug) {
      console.debug('[mixpanel] track (noop)', event, properties);
    }
    return;
  }

  mixpanel.track(event, {
    ...baseProps(),
    ...stripSensitiveProps(properties as Record<string, unknown> | undefined),
  });
}

export function identifyMixpanelUser(traits: UserTraits): void {
  if (!initialized) return;

  const { user_id, email, name, ...rest } = traits;
  const anonId = getAnonymousId();

  if (anonId && anonId !== user_id) {
    mixpanel.alias(user_id, anonId);
  }

  mixpanel.identify(user_id);
  mixpanel.people.set({
    $email: email,
    $name: name,
    ...rest,
    last_seen: new Date().toISOString(),
  });
}

export function resetMixpanelAnalytics(): void {
  if (!initialized) return;
  mixpanel.reset();
  if (typeof window !== 'undefined') {
    localStorage.removeItem(ANON_ID_KEY);
  }
}

export const MixpanelAnalytics = {
  init: initMixpanelAnalytics,
  track: trackMixpanelEvent,
  identify: identifyMixpanelUser,
  reset: resetMixpanelAnalytics,
  consent: setMixpanelConsent,
  hasConsent: hasMixpanelConsent,
  getAnonymousId,

  userSignedUp: (props: EventPropertiesMap['user_signed_up']) =>
    trackMixpanelEvent(AnalyticsEvents.USER_SIGNED_UP, props),

  userLoggedIn: (props: EventPropertiesMap['user_logged_in']) =>
    trackMixpanelEvent(AnalyticsEvents.USER_LOGGED_IN, props),

  projectCreated: (props: EventPropertiesMap['project_created']) =>
    trackMixpanelEvent(AnalyticsEvents.PROJECT_CREATED, props),

  aiAgentCreated: (props: EventPropertiesMap['ai_agent_created']) =>
    trackMixpanelEvent(AnalyticsEvents.AI_AGENT_CREATED, props),

  messageSent: (props: EventPropertiesMap['message_sent']) =>
    trackMixpanelEvent(AnalyticsEvents.MESSAGE_SENT, props),

  subscriptionUpgraded: (props: EventPropertiesMap['subscription_upgraded']) =>
    trackMixpanelEvent(AnalyticsEvents.SUBSCRIPTION_UPGRADED, props),

  subscriptionCancelled: (props: EventPropertiesMap['subscription_cancelled']) =>
    trackMixpanelEvent(AnalyticsEvents.SUBSCRIPTION_CANCELLED, props),

  pageViewed: (props: EventPropertiesMap['page_viewed']) =>
    trackMixpanelEvent(AnalyticsEvents.PAGE_VIEWED, props),
};
